import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Folder, Trash2, UploadCloud, ZoomIn, ZoomOut } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Cover image sub-dialog — opened from ProjectSettingsDialog.
 *
 * Locked 2026-07-13. Two-step upload flow (decided with user):
 *   1. This dialog only STAGES a File → cropped Blob. Nothing hits R2 yet.
 *   2. Outer Save (in ProjectSettingsDialog) presigns → PUTs → PATCHes.
 *
 * States:
 *   - Empty (no `initialImageUrl` and nothing picked): drop zone + click-to-upload.
 *   - Preview: image inside a fixed square viewport with pan + zoom (1x–3x).
 *     Right rail exposes folder-icon (change image) + trash (remove entirely).
 *
 * Canvas crop math on Save — VP = viewport px, s = actualScale, (x,y) = pan
 * offset in screen px:
 *   sx = imgW/2 − (VP/2 + x)/s
 *   sy = imgH/2 − (VP/2 + y)/s
 *   sw = sh = VP/s
 * drawImage(img, sx, sy, sw, sh, 0, 0, OUT, OUT) with OUT = 800.
 */
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

// Crop aspect matches the project card (aspect-video, 16:9) so the sub-dialog
// preview IS the final card image — no letterbox, no hidden trim. Locked
// 2026-07-13 after user flagged the square-vs-video mismatch.
const VIEWPORT_W = 448;
const VIEWPORT_H = 252;   // 448 * 9/16 = 252
const OUTPUT_W   = 1280;
const OUTPUT_H   = 720;   // 1280 * 9/16
const OUTPUT_QUALITY = 0.9; // JPEG quality on final crop
const MIN_ZOOM = 1;
const MAX_ZOOM = 3; // upper bound for the zoom slider

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** URL of the current saved cover (if any) so users can see what they're replacing. */
  initialImageUrl?: string | null;
  /** Fires on Save. `blob` = null means "remove the cover" (only when initial was set). */
  onCommit: (result: { blob: Blob | null; previewUrl: string | null }) => void;
};

export function ProjectCoverImageDialog({
  open,
  onOpenChange,
  initialImageUrl,
  onCommit,
}: Props) {
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pickedBlobUrl, setPickedBlobUrl] = useState<string | null>(null); // blob: url we own
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragStateRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);

  // Reset every time the dialog opens. Start from the saved cover if one exists;
  // otherwise show the empty state.
  useEffect(() => {
    if (open) {
      setImgUrl(initialImageUrl ?? null);
      setZoom(MIN_ZOOM);
      setPan({ x: 0, y: 0 });
      setError(null);
      setIsDragOver(false);
    }
  }, [open, initialImageUrl]);

  // Own blob: URLs. Revoke when we swap or unmount.
  useEffect(() => {
    return () => {
      if (pickedBlobUrl) URL.revokeObjectURL(pickedBlobUrl);
    };
  }, [pickedBlobUrl]);

  // Load the HTMLImageElement whenever imgUrl changes so we can measure natural
  // dimensions for base-scale math + canvas crop.
  useEffect(() => {
    if (!imgUrl) {
      setImgEl(null);
      return;
    }
    const el = new Image();
    el.crossOrigin = "anonymous"; // R2 CDN allows this; blob: URLs ignore it.
    el.onload = () => setImgEl(el);
    el.onerror = () => setError("Couldn't load the image.");
    el.src = imgUrl;
    return () => {
      el.onload = null;
      el.onerror = null;
    };
  }, [imgUrl]);

  const baseScale = useMemo(() => {
    if (!imgEl) return 1;
    // "cover" fit at min zoom — image always fully covers the 16:9 viewport.
    return Math.max(VIEWPORT_W / imgEl.naturalWidth, VIEWPORT_H / imgEl.naturalHeight);
  }, [imgEl]);

  const actualScale = baseScale * zoom;

  // Clamp pan so the image edges can't leave the viewport — matches Frame.io.
  const clampPan = useCallback(
    (px: number, py: number) => {
      if (!imgEl) return { x: 0, y: 0 };
      const maxX = Math.max(0, (imgEl.naturalWidth * actualScale - VIEWPORT_W) / 2);
      const maxY = Math.max(0, (imgEl.naturalHeight * actualScale - VIEWPORT_H) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, px)),
        y: Math.min(maxY, Math.max(-maxY, py)),
      };
    },
    [imgEl, actualScale]
  );

  // Re-clamp pan whenever zoom changes (zooming out shrinks the pan bound).
  useEffect(() => {
    setPan((p) => clampPan(p.x, p.y));
  }, [clampPan]);

  function acceptFile(file: File) {
    if (!ACCEPTED.includes(file.type)) {
      setError("Only JPEG, PNG, or WebP images are accepted.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError("Image must be 10 MB or smaller.");
      return;
    }
    setError(null);
    // Swap the blob: URL — revoke the old one via the pickedBlobUrl cleanup effect.
    const url = URL.createObjectURL(file);
    setPickedBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    setImgUrl(url);
    setZoom(MIN_ZOOM);
    setPan({ x: 0, y: 0 });
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow selecting the same file again
    if (file) acceptFile(file);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) acceptFile(file);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(false);
  }

  function beginDrag(clientX: number, clientY: number) {
    dragStateRef.current = { startX: clientX, startY: clientY, panX: pan.x, panY: pan.y };
  }

  function moveDrag(clientX: number, clientY: number) {
    const s = dragStateRef.current;
    if (!s) return;
    const nextX = s.panX + (clientX - s.startX);
    const nextY = s.panY + (clientY - s.startY);
    setPan(clampPan(nextX, nextY));
  }

  function endDrag() {
    dragStateRef.current = null;
  }

  // Bind mousemove/mouseup on window while dragging so we don't lose the drag
  // when the pointer briefly leaves the viewport.
  useEffect(() => {
    if (!imgEl) return;
    const onMove = (e: MouseEvent) => moveDrag(e.clientX, e.clientY);
    const onUp = () => endDrag();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgEl, clampPan]);

  function handleRemove() {
    if (pickedBlobUrl) {
      URL.revokeObjectURL(pickedBlobUrl);
      setPickedBlobUrl(null);
    }
    setImgUrl(null);
    setImgEl(null);
    setError(null);
    setZoom(MIN_ZOOM);
    setPan({ x: 0, y: 0 });
  }

  async function handleSave() {
    // No image at all — either the user cleared it or never picked one.
    // If there was an initial cover, this means "remove it".
    if (!imgEl) {
      onCommit({ blob: null, previewUrl: null });
      onOpenChange(false);
      return;
    }

    // Compute the source crop rect from the current pan + zoom, then draw
    // into a 16:9 1280×720 canvas and export as JPEG.
    const s = actualScale;
    const sw = VIEWPORT_W / s;
    const sh = VIEWPORT_H / s;
    const sx = imgEl.naturalWidth / 2 - (VIEWPORT_W / 2 + pan.x) / s;
    const sy = imgEl.naturalHeight / 2 - (VIEWPORT_H / 2 + pan.y) / s;

    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_W;
    canvas.height = OUTPUT_H;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setError("Canvas rendering isn't available in this browser.");
      return;
    }
    // Solid white behind in case a transparent PNG was uploaded — JPEG has no alpha.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, OUTPUT_W, OUTPUT_H);
    ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, OUTPUT_W, OUTPUT_H);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", OUTPUT_QUALITY);
    });
    if (!blob) {
      setError("Couldn't produce the cover image. Try a different photo.");
      return;
    }

    const previewUrl = URL.createObjectURL(blob);
    onCommit({ blob, previewUrl });
    onOpenChange(false);
  }

  const hasImage = !!imgEl;
  const showRemoveButton = hasImage || !!initialImageUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cover photo</DialogTitle>
        </DialogHeader>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED.join(",")}
          className="hidden"
          onChange={handleFileChange}
        />

        {!hasImage ? (
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={cn(
              "flex aspect-video w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed transition-colors",
              isDragOver
                ? "border-primary bg-primary/5"
                : "border-border bg-secondary/40 hover:bg-secondary"
            )}
          >
            <UploadCloud className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
            <p className="text-center text-sm text-muted-foreground">
              Drag and drop or click to upload image
            </p>
            <p className="text-[11px] text-muted-foreground">JPEG, PNG, WebP · up to 10 MB</p>
          </div>
        ) : (
          <div className="relative">
            <div
              className="relative mx-auto overflow-hidden rounded-xl border border-border bg-[repeating-conic-gradient(#e5e7eb_0%_25%,transparent_0%_50%)] bg-[length:20px_20px]"
              style={{ width: VIEWPORT_W, height: VIEWPORT_H, maxWidth: "100%" }}
            >
              <img
                src={imgUrl ?? undefined}
                alt=""
                draggable={false}
                onMouseDown={(e) => {
                  e.preventDefault();
                  beginDrag(e.clientX, e.clientY);
                }}
                className="absolute left-1/2 top-1/2 cursor-grab select-none active:cursor-grabbing"
                style={{
                  // Explicit maxWidth/maxHeight none — Tailwind preflight sets
                  // `img { max-width: 100%; height: auto }`, which clipped the img
                  // width to the container while leaving the inline height alone.
                  // Result: box aspect = 384/naturalH instead of naturalW/naturalH
                  // → horizontal squish in the preview. Locked 2026-07-13.
                  width: imgEl?.naturalWidth,
                  height: imgEl?.naturalHeight,
                  maxWidth: "none",
                  maxHeight: "none",
                  transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${actualScale})`,
                  transformOrigin: "center center",
                }}
              />

              {/* Right rail — change / delete */}
              <div className="absolute right-3 top-3 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="grid h-9 w-9 place-items-center rounded-md bg-background/90 text-foreground shadow-sm ring-1 ring-border hover:bg-background"
                  title="Change image"
                >
                  <Folder className="h-4 w-4" strokeWidth={1.75} />
                </button>
                {showRemoveButton && (
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="grid h-9 w-9 place-items-center rounded-md bg-background/90 text-destructive shadow-sm ring-1 ring-border hover:bg-background"
                    title="Remove image"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {hasImage && (
          <div className="flex items-center gap-3">
            <ZoomOut className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            <input
              type="range"
              min={MIN_ZOOM * 100}
              max={MAX_ZOOM * 100}
              step={1}
              value={Math.round(zoom * 100)}
              onChange={(e) => setZoom(Number(e.target.value) / 100)}
              className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-secondary accent-primary"
              aria-label="Zoom"
            />
            <ZoomIn className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
